/**
 * The form-state shape both software-release forms use for their per-component version selects.
 *
 * Shared by `SoftwareReleaseCreate` and `SoftwareReleaseEdit` since TVR-2156, so the two cannot
 * drift apart again. Before that the create form registered each select under the component's own
 * **name** (`tover.tafel`), which react-hook-form reads as a *path* and nests under a `tover`
 * object, and its toolbar dug the value back out with `tover[name.split('.')[1]]`. That only worked
 * while every component was named `<something>.<something>` with `tover` as the first segment:
 * `updater` yielded `undefined`, `tover.tafel.core` yielded a nested object instead of an id, and
 * `a.core` and `b.core` would have collided on the same key. `undefined` there is silent — it just
 * means no version submitted.
 *
 * Keying by component **id** under one flat namespace removes all four cases at once: ids are
 * unique, cannot contain a dot, and depend on no naming convention. The `c` prefix keeps the key
 * from being read as an array index, so react-hook-form builds an object rather than a sparse array.
 * A side effect worth keeping: `component_versions.c5` is not a valid dotted translation key, so it
 * no longer triggers an incidental polyglot lookup on every render the way `tover.tafel` did.
 *
 * **Not** shared: each form's `SUBMITTED_FIELDS` allow-list. Those describe two different backend
 * FormRequests (`CreateRequest` and `UpdateRequest`) that are free to diverge — see the comment on
 * each.
 */
export const COMPONENT_VERSIONS = 'component_versions';

/** Key of one component inside that namespace — `c` prefixed so it is never read as an index. */
export const versionKey = (component: { id: number }): string => `c${component.id}`;

/** The full react-hook-form path an input for one component writes to. */
export const versionFieldName = (component: { id: number }): string => `${COMPONENT_VERSIONS}.${versionKey(component)}`;

/** The value of the `component_versions` namespace: a version id per `versionKey`, or nothing. */
export type ComponentVersions = Record<string, number | null | undefined>;
